import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { doc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.bg};
`;

const Card = styled.div`
  width: 100%;
  max-width: 400px;
  margin: 16px;
  padding: 40px 32px;
  background: #fff;
  border-radius: ${({ theme }) => theme.radius.lg};
  box-shadow: ${({ theme }) => theme.shadow.lg};
`;

const Title = styled.h2`
  text-align: center;
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 8px;
`;

const Desc = styled.p`
  text-align: center;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 28px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 14px;
  margin-bottom: 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 14px;
  outline: none;
  &:focus { border-color: ${({ theme }) => theme.colors.primary}; }
`;

const Button = styled.button`
  width: 100%;
  padding: 12px;
  margin-top: 8px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  border-radius: ${({ theme }) => theme.radius.sm};
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const ErrorMsg = styled.p`
  color: ${({ theme }) => theme.colors.danger};
  font-size: 13px;
  text-align: center;
  margin-top: 12px;
`;

const ChangePasswordPage = () => {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const isTemp = !!userData?.tempPassword;
  const [currentPw, setCurrentPw] = useState('');
  const [pw, setPw] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isTemp && !currentPw) return setError('현재 비밀번호를 입력해주세요.');
    if (pw.length < 6) return setError('새 비밀번호는 6자 이상이어야 합니다.');
    if (pw !== pwConfirm) return setError('새 비밀번호가 일치하지 않습니다.');

    setLoading(true);
    setError('');
    try {
      if (!isTemp && auth.currentUser?.email) {
        const cred = EmailAuthProvider.credential(auth.currentUser.email, currentPw);
        await reauthenticateWithCredential(auth.currentUser, cred);
      }
      await updatePassword(auth.currentUser, pw);
      if (userData?.uid) {
        await updateDoc(doc(db, 'users', userData.uid), { tempPassword: false });
      }
      alert('비밀번호가 변경되었습니다.');
      navigate('/', { replace: true });
    } catch (err) {
      if (err?.code === 'auth/wrong-password' || err?.code === 'auth/invalid-credential') {
        setError('현재 비밀번호가 올바르지 않습니다.');
      } else {
        setError('비밀번호 변경에 실패했습니다.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Wrapper>
      <Card>
        <Title>비밀번호 변경</Title>
        <Desc>{isTemp ? '임시 비밀번호를 변경해주세요.' : '현재 비밀번호 확인 후 새 비밀번호로 변경합니다.'}</Desc>
        <form onSubmit={handleSubmit}>
          {!isTemp && (
            <Input
              type="password"
              placeholder="현재 비밀번호"
              value={currentPw}
              onChange={(e) => setCurrentPw(e.target.value)}
              required
            />
          )}
          <Input
            type="password"
            placeholder="새 비밀번호 (6자 이상)"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            required
          />
          <Input
            type="password"
            placeholder="새 비밀번호 확인"
            value={pwConfirm}
            onChange={(e) => setPwConfirm(e.target.value)}
            required
          />
          <Button type="submit" disabled={loading}>
            {loading ? '변경 중...' : '비밀번호 변경'}
          </Button>
        </form>
        {error && <ErrorMsg>{error}</ErrorMsg>}
      </Card>
    </Wrapper>
  );
};

export default ChangePasswordPage;
